import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Cashpurr, CashpurrMessage } from '@/components/ui/cashpurr';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { supabase } from '@/integrations/supabase/client';
import { Link, useNavigate } from 'react-router-dom';
import { Target, CheckSquare, Book, BarChart3, Users, Calendar, Loader2, Share2, MessageSquare } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { achievementService } from '@/services/achievementService';

interface Profile {
  id: string;
  user_id: string;
  email: string;
  idea_description?: string;
  development_stage?: string;
  monthly_tool_spend?: number;
  tool_expenses?: any[];
  onboarding_completed: boolean;
  created_at?: string;
  points?: number;
  streak_days?: number;
  badges?: any[] | null;
  last_checkin?: string | null;
}

interface Task {
  id: string;
  title: string;
  description: string | null;
  sprint_day: number;
  completed: boolean;
  due_date: string | null;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  useNotifications(); // Initialize notification system
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [sprintDay, setSprintDay] = useState(1);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [totalTasks, setTotalTasks] = useState(30);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [dailyCheckin, setDailyCheckin] = useState('');
  const [socialStreak, setSocialStreak] = useState(0);
  const [achievements, setAchievements] = useState<any[]>([]);

  useEffect(() => {
    if (user) {
      fetchProfile();
      // Check and award streak badges on dashboard load
      achievementService.checkStreakBadges(user.id);
    }
  }, [user]);

  useEffect(() => {
    if (profile && !profile.onboarding_completed) {
      navigate('/app/idea-validation');
    }
  }, [profile, navigate]);

  const fetchProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        // Create profile if it doesn't exist
        if (error.code === 'PGRST116') {
          await createProfile();
          return;
        }
      } else {
        setProfile({ ...data, email: user?.email || '' });
        if (data && data.onboarding_completed) {
          // Calculate sprint day based on onboarding completion date
          const onboardingDate = new Date(data.created_at);
          const currentDate = new Date();
          const daysSinceOnboarding = Math.floor((currentDate.getTime() - onboardingDate.getTime()) / (1000 * 60 * 60 * 24));
          // Sprint day starts at 1 on the day of onboarding completion
          const calculatedSprintDay = Math.min(Math.max(1, daysSinceOnboarding + 1), 30);
          setSprintDay(calculatedSprintDay);
          
          fetchTasks();
          fetchSocialStreak();
          fetchAchievements();
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          user_id: user.id,
          email: user.email || '',
          idea_description: null,
          development_stage: 'idea',
          monthly_tool_spend: 0,
          tool_expenses: null,
          onboarding_completed: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      
      setProfile({ ...data, email: user?.email || '' });
      // Award welcome badge on profile creation
      await achievementService.awardWelcomeBadge(user.id);
      navigate('/app/idea-validation');
    } catch (error) {
      console.error('Error creating profile:', error);
    }
  };

  const fetchTasks = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, description, sprint_day, completed, due_date')
        .eq('user_id', user.id)
        .order('sprint_day', { ascending: true });

      if (error) {
        console.error('Error fetching tasks:', error);
      } else {
        // Remove duplicates based on task id
        const uniqueTasks = data ? data.filter((task, index, self) => 
          index === self.findIndex(t => t.id === task.id)
        ) : [];
        
        setTasks(uniqueTasks);
        setTotalTasks(uniqueTasks.length);
        setCompletedTasks(uniqueTasks.filter(task => task.completed).length);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchSocialStreak = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('social_logs')
        .select('date')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      // Calculate streak
      if (data && data.length > 0) {
        let currentStreak = 0;
        const today = new Date();
        
        for (let i = 0; i < data.length; i++) {
          const logDate = new Date(data[i].date);
          const expectedDate = new Date(today);
          expectedDate.setDate(today.getDate() - i);
          
          if (logDate.toDateString() === expectedDate.toDateString()) {
            currentStreak++;
          } else {
            break;
          }
        }
        
        setSocialStreak(currentStreak);
      } else {
        setSocialStreak(0);
      }
    } catch (error) {
      console.error('Error fetching social streak:', error);
    }
  };

  const fetchAchievements = async () => {
    if (!user) return;
    
    try {
      const userAchievements = await achievementService.getUserAchievements(user.id);
      setAchievements(userAchievements);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
  };

  const getCashpurrMessage = () => {
    const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    
    if (progress === 0) {
      return "Welcome to your sprint! Let's get started with your first task. 🚀";
    } else if (progress < 25) {
      return "Good start! Keep the momentum going. Every task completed brings you closer to revenue! 💪";
    } else if (progress < 50) {
      return "You're making progress! Stay focused and keep pushing forward. 🎯";
    } else if (progress < 75) {
      return "Excellent work! You're more than halfway there. Don't slow down now! 🔥";
    } else if (progress < 100) {
      return "Almost there! You're in the final stretch. Finish strong! 🏁";
    } else {
      return "Incredible! You've completed all tasks. Time to celebrate and plan your next sprint! 🎉";
    }
  };

  const getCashpurrMood = () => {
    const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    
    if (progress === 0) return "excited";
    if (progress < 25) return "happy";
    if (progress < 50) return "thinking";
    if (progress < 75) return "excited";
    if (progress < 100) return "happy";
    return "excited";
  };

  const toggleTask = async (taskId: string, current: boolean) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ completed: !current })
        .eq('id', taskId);
      if (error) throw error;
      const nextTasks = tasks.map(t => t.id === taskId ? { ...t, completed: !current } : t);
      setTasks(nextTasks);
      setCompletedTasks(nextTasks.filter(t => t.completed).length);

      if (!current) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);

        // Gamification: award points and update streak
        try {
          const today = new Date().toISOString().slice(0,10);
          const last = profile?.last_checkin?.slice(0,10);
          const isNewDay = last !== today;
          const nextStreak = isNewDay ? (profile?.streak_days || 0) + 1 : (profile?.streak_days || 0);
          const earnedBadge = nextStreak === 7 ? 'Sprint Star' : null;

          const updates: any = {
            points: (profile?.points || 0) + 10,
            last_checkin: today,
            streak_days: nextStreak,
            updated_at: new Date().toISOString(),
          };
          if (earnedBadge) {
            const currentBadges = (profile?.badges as any[]) || [];
            updates.badges = [...currentBadges, { name: earnedBadge, awarded_at: new Date().toISOString() }];
          }
          await supabase.from('profiles').update(updates).eq('user_id', user!.id);
          setProfile(prev => prev ? { ...prev, ...updates } as Profile : prev);

          if (earnedBadge) {
            // Store badge notification in cashpurr_chats instead of notifications table
            await supabase.from('cashpurr_chats').insert({
              user_id: user!.id,
              sender: 'cashpurr',
              content: `🏅 You earned the ${earnedBadge} badge! 7-day streak—keep clawing forward!`,
              timestamp: new Date().toISOString(),
              context: { source: 'badge_earned', badge: earnedBadge }
            });
          }
        } catch (ge) {
          console.error('Gamification update failed', ge);
        }
      }
    } catch (e) {
      console.error('Failed to toggle task', e);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Cashpurr size="xl" mood="thinking" className="mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
          <p className="text-muted-foreground">
            Loading your sprint progress...
          </p>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Loader2 className="w-12 h-12 mx-auto text-muted-foreground mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!profile || !profile.onboarding_completed) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Cashpurr size="xl" mood="thinking" className="mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
          <p className="text-muted-foreground">
            Redirecting to idea validation...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center mb-4">
          <Cashpurr size="xl" mood={getCashpurrMood()} />
        </div>
        <h1 className="text-4xl font-bold mb-2">Your 30-Day Sprint Plan</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          {getCashpurrMessage()}
        </p>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-primary">
              {sprintDay}
            </CardTitle>
            <CardDescription>Current Sprint Day</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-green-600">
              {completedTasks}
            </CardTitle>
            <CardDescription>Tasks Completed</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-blue-600">
              {totalTasks - completedTasks}
            </CardTitle>
            <CardDescription>Tasks Remaining</CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Sprint Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="w-5 h-5" />
            <span>Sprint Progress</span>
          </CardTitle>
          <CardDescription>
            Track your progress through the 30-day sprint
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm text-muted-foreground">
              {Math.round((completedTasks / totalTasks) * 100)}% Complete
            </span>
          </div>
          <Progress value={(completedTasks / totalTasks) * 100} className="h-3" />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Idea Validation</span>
            <span>Launch</span>
            <span>First Revenue</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Sprint Day {sprintDay}</span>
                <span className="text-sm text-muted-foreground">
                  {Math.round((sprintDay / 30) * 100)}% of Sprint
                </span>
              </div>
              <Progress value={(sprintDay / 30) * 100} className="h-2" />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Task Completion</span>
                <span className="text-sm text-muted-foreground">
                  {completedTasks}/{totalTasks}
                </span>
              </div>
              <Progress value={(completedTasks / totalTasks) * 100} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Achievements & Streaks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Achievements</CardTitle>
            <CardDescription>Badges you have earned</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {achievements.length > 0 ? (
                achievements.map((achievement: any, idx: number) => (
                  <span key={idx} className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200 text-sm flex items-center gap-1">
                    {achievement.icon} {achievement.badge_name.replace(/_/g, ' ')}
                  </span>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No badges yet. Complete tasks to earn your first badge!</p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Sprint Progress</CardTitle>
            <CardDescription>Your 30-day journey metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-lg border">
                <div className="text-2xl font-bold text-primary">{profile?.points || 0}</div>
                <div className="text-sm text-muted-foreground">Points</div>
              </div>
              <div className="p-4 rounded-lg border">
                <div className="text-2xl font-bold text-green-600">{sprintDay}</div>
                <div className="text-sm text-muted-foreground">Days Worked</div>
              </div>
              <div className="p-4 rounded-lg border">
                <div className="text-2xl font-bold text-blue-600">{Math.max(0, 30 - sprintDay)}</div>
                <div className="text-sm text-muted-foreground">Days Left</div>
              </div>
            </div>
            <div className="mt-4 text-center">
              <div className="text-sm text-muted-foreground">
                Days Worked: <span className="font-medium text-green-600">{sprintDay}</span> | 
                Days Left: <span className="font-medium text-blue-600">{Math.max(0, 30 - sprintDay)}</span>
              </div>
              <Progress value={(sprintDay / 30) * 100} className="mt-2" />
            </div>
            {socialStreak > 0 && (
              <div className="mt-3 text-center">
                <Link to="/app/social" className="text-sm text-blue-600 hover:underline flex items-center justify-center space-x-1">
                  <Share2 className="w-4 h-4" />
                  <span>View Social Activity</span>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Link to="/app/validation">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <Target className="w-8 h-8 mx-auto mb-3 text-blue-600" />
              <h3 className="font-semibold mb-1">Validate Idea</h3>
              <p className="text-sm text-muted-foreground">Get AI feedback</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/boss">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <CheckSquare className="w-8 h-8 mx-auto mb-3 text-green-600" />
              <h3 className="font-semibold mb-1">Boss Mode</h3>
              <p className="text-sm text-muted-foreground">Daily tasks</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/playbooks">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <Book className="w-8 h-8 mx-auto mb-3 text-purple-600" />
              <h3 className="font-semibold mb-1">Playbooks</h3>
              <p className="text-sm text-muted-foreground">Launch templates</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/social">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <Share2 className="w-8 h-8 mx-auto mb-3 text-blue-600" />
              <h3 className="font-semibold mb-1">Social Tracker</h3>
              <p className="text-sm text-muted-foreground">Build audience</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/prompts">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <MessageSquare className="w-8 h-8 mx-auto mb-3 text-yellow-600" />
              <h3 className="font-semibold mb-1">Prompt Library</h3>
              <p className="text-sm text-muted-foreground">AI prompts</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/roi">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <BarChart3 className="w-8 h-8 mx-auto mb-3 text-orange-600" />
              <h3 className="font-semibold mb-1">ROI Tracker</h3>
              <p className="text-sm text-muted-foreground">Track expenses</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Tasks */}
      {tasks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Recent Tasks</span>
            </CardTitle>
            <CardDescription>
              Your latest sprint activities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {tasks.slice(0, 5).map((task) => (
                <div
                  key={task.id}
                  className={`flex items-center space-x-3 p-3 rounded-lg border ${
                    task.completed ? 'bg-green-50 border-green-200' : 'bg-muted/50'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                    task.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}>
                    {task.completed && <CheckSquare className="w-4 h-4 text-white" />}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-medium ${task.completed ? 'line-through text-gray-600' : ''}`}>
                      {task.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Day {task.sprint_day} • {task.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {tasks.length > 5 && (
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" onClick={() => setShowAllTasks(true)}>
                  View All Tasks
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cashpurr Message */}
      <Card>
        <CardContent className="text-center py-6">
          <div className="flex items-center justify-center space-x-3 mb-3">
            <Cashpurr size="md" mood={getCashpurrMood()} />
            <p className="text-lg font-medium">
              Keep pushing forward! Every completed task brings you closer to your first sale. 😸
            </p>
          </div>
          <p className="text-muted-foreground">
            Remember, consistency beats perfection. Focus on progress, not perfection.
          </p>
        </CardContent>
      </Card>

      {/* Daily Check-in & Roadmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Daily Check-in</CardTitle>
            <CardDescription>How's the sprint? Tell Cashpurr and get a nudge.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="What will you accomplish today?"
                value={dailyCheckin}
                onChange={(e) => setDailyCheckin(e.target.value)}
              />
              <Button
                onClick={async () => {
                  if (!dailyCheckin.trim() || !user) return;
                  try {
                    await supabase.from('cashpurr_chats').insert({
                      user_id: user.id,
                      sender: 'user',
                      content: `Daily check-in: ${dailyCheckin}`,
                      timestamp: new Date().toISOString(),
                      context: { type: 'daily_checkin' },
                    });
                    setDailyCheckin('');
                    const event = new CustomEvent('open-cashpurr', { detail: { prefill: `I plan to: ${dailyCheckin}. Any advice to maximize impact today?` } });
                    window.dispatchEvent(event);
                  } catch (e) {
                    console.error('Failed check-in', e);
                  }
                }}
              >
                Check-in
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Roadmap</CardTitle>
            <CardDescription>Key milestones on your path to revenue</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 rounded-lg border">
                <div className="text-sm font-medium">Validate</div>
                <div className="text-xs text-muted-foreground">Idea validated</div>
              </div>
              <div className="p-3 rounded-lg border">
                <div className="text-sm font-medium">Launch</div>
                <div className="text-xs text-muted-foreground">MVP shipped</div>
              </div>
              <div className="p-3 rounded-lg border">
                <div className="text-sm font-medium">Revenue</div>
                <div className="text-xs text-muted-foreground">First sale</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* All Tasks Modal */}
      <Dialog open={showAllTasks} onOpenChange={setShowAllTasks}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden p-0">
          <DialogHeader className="p-6 pb-2 border-b">
            <DialogTitle className="text-xl font-bold">Your 30-Day Task List</DialogTitle>
          </DialogHeader>
          <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]" style={{ scrollBehavior: 'smooth' }}>
            <Accordion type="multiple" className="w-full">
              {Array.from(new Set(tasks.map(t => t.sprint_day))).sort((a,b)=>a-b).map(day => (
                <AccordionItem key={day} value={`day-${day}`}>
                  <AccordionTrigger>
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">Day {day}</span>
                        <span className="text-xs text-muted-foreground">{tasks.filter(t=>t.sprint_day===day && t.completed).length}/{tasks.filter(t=>t.sprint_day===day).length} done</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3">
                      {tasks.filter(t=>t.sprint_day===day).map(task => (
                        <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg border bg-background">
                          <Checkbox checked={task.completed} onCheckedChange={() => toggleTask(task.id, task.completed)} />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.title}</h4>
                              {task.due_date && <span className="text-xs text-muted-foreground">Due {new Date(task.due_date).toLocaleDateString()}</span>}
                            </div>
                            {task.description && <p className="text-sm text-muted-foreground">{task.description}</p>}
                            <p className="text-xs mt-1 text-blue-700">Cashpurr: Complete this to unlock your next milestone!</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </DialogContent>
      </Dialog>

      {showConfetti && (
        <div className="pointer-events-none fixed inset-0 flex items-center justify-center">
          <div className="animate-bounce text-3xl">🎉</div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;